import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { format, formatDistanceToNow } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import {
  StatsCard,
  SkeletonCard,
  SkeletonChart,
  EmptyState,
  type DateRange,
} from "../../features/statsNew/components";
import SessionsPerMonthChart from "../../features/statsNew/components/charts/SessionsPerMonthChart";
import TopParcoursChart from "../../features/statsNew/components/charts/TopParcoursChart";
import { ActivityHeatmap } from "../../features/statsNew/components/charts";
import { getSessionDate, isSessionInDateRange } from "../../features/statsNew/utils/dateUtils";
import {
  getAllSessionsByProjectGroup,
  sessionsByProjectGroupSelector,
} from "../../features/stats/state/statsSlice";
import { AppDispatch } from "../../common/state/store";
import { setDateRange } from "../../features/statsNew/state/statsNewSlice";
import { getNumberOfPaywalledAnonymousSessions } from "../../features/subscription/state/subscriptionSlice";

interface OutletContextType {
  dateRange: DateRange;
  searchValue: string;
  sortKey: string;
}

const GlobalView = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { t, i18n } = useTranslation();
  const { dateRange } = useOutletContext<OutletContextType>();
  const sessionsByProjectGroup = useSelector(sessionsByProjectGroupSelector);
  const numberOfPaywalledAnonymousSessions = useSelector(getNumberOfPaywalledAnonymousSessions);
  const locale = i18n.language === "fr" ? fr : enUS;

  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Check if the alert banner is visible
  const showAlertBanner = numberOfPaywalledAnonymousSessions > 0;

  // CSS class for alert-aware responsive height
  const chartContainerClass = `global-chart-container ${showAlertBanner ? "with-alert" : ""}`;
  const pageSize = showAlertBanner ? 9 : 10;

  useEffect(() => {
    dispatch(getAllSessionsByProjectGroup({ componentId: "statsNewGlobalView" }));
  }, [dispatch]);

  // Track when data has loaded at least once
  useEffect(() => {
    if (sessionsByProjectGroup.length > 0 && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [sessionsByProjectGroup, isInitialLoad]);

  const handleClearDateRange = () => {
    dispatch(setDateRange({ startDate: null, endDate: null }));
  };

  // Check if there are any sessions at all (without date filter)
  const hasAnySessionsWithoutFilter = useMemo(() => {
    return sessionsByProjectGroup.some((group) =>
      group.projects.some((project) => project.sessions.length > 0),
    );
  }, [sessionsByProjectGroup]);

  // Helper function to count sessions in a date range
  const countSessionsInRange = useCallback(
    (startDate: Date | null, endDate: Date | null) => {
      let count = 0;
      sessionsByProjectGroup.forEach((group) => {
        group.projects.forEach((project) => {
          project.sessions.forEach((session) => {
            const sessionDate = getSessionDate(session);
            if (startDate && sessionDate < startDate) return;
            if (endDate && sessionDate > endDate) return;
            count++;
          });
        });
      });
      return count;
    },
    [sessionsByProjectGroup],
  );

  // Calculate previous period for trend comparison
  const previousPeriod = useMemo(() => {
    const formatDateShort = (date: Date) => format(date, "d MMM", { locale });

    if (!dateRange.startDate || !dateRange.endDate) {
      // No date range selected: compare last 30 days vs 30 days before
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      const sixtyDaysAgo = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);
      return {
        startDate: sixtyDaysAgo,
        endDate: thirtyDaysAgo,
        label: `vs ${formatDateShort(sixtyDaysAgo)} - ${formatDateShort(thirtyDaysAgo)}`,
      };
    }

    // Calculate the duration of the selected period
    const duration = dateRange.endDate.getTime() - dateRange.startDate.getTime();
    const previousStartDate = new Date(dateRange.startDate.getTime() - duration);
    const previousEndDate = new Date(dateRange.startDate.getTime() - 1); // Day before current start

    return {
      startDate: previousStartDate,
      endDate: previousEndDate,
      label: `vs ${formatDateShort(previousStartDate)} - ${formatDateShort(previousEndDate)}`,
    };
  }, [dateRange, locale]);

  // Compute statistics based on date range
  const stats = useMemo(() => {
    let totalSessions = 0;
    let lastSessionDate: Date | null = null;
    let lastParcoursName = "-";
    let lastParcoursId: number | null = null;
    let lastModuleName = "-";
    let lastModuleId: number | null = null;
    let lastSessionId: number | null = null;
    let lastSessionUserName: string | null = null;

    // Track sessions per parcours and module durations
    const parcoursSessionCounts = new Map<number, number>();
    const moduleDurations: number[] = [];
    let lastModuleIdForDuration: number | null = null;

    sessionsByProjectGroup.forEach((group) => {
      group.projects.forEach((project) => {
        project.sessions.forEach((session) => {
          const sessionDate = getSessionDate(session);

          // Apply date range filter
          if (!isSessionInDateRange(sessionDate, dateRange)) return;

          totalSessions++;

          // Count sessions per parcours
          const currentCount = parcoursSessionCounts.get(group.project_group_id) || 0;
          parcoursSessionCounts.set(group.project_group_id, currentCount + 1);

          // Track most recent session
          if (!lastSessionDate || sessionDate > lastSessionDate) {
            lastSessionDate = sessionDate;
            lastParcoursName = group.project_group_name;
            lastParcoursId = group.project_group_id;
            lastModuleName = project.project_name || "-";
            lastModuleId = project.project_id;
            lastSessionId = session.id;
            lastModuleIdForDuration = project.project_id;
            // Get user name from session
            lastSessionUserName = session.enduser
              ? `${session.enduser.firstname || ""} ${session.enduser.lastname || ""}`.trim() ||
                session.enduser.username ||
                session.enduser.email ||
                null
              : session.user_name || null;
          }
        });
      });
    });

    // Calculate average duration for last module
    let lastModuleAvgDuration: number | null = null;
    if (lastModuleIdForDuration) {
      sessionsByProjectGroup.forEach((group) => {
        group.projects.forEach((project) => {
          if (project.project_id === lastModuleIdForDuration) {
            project.sessions.forEach((session) => {
              const sessionDate = getSessionDate(session);
              if (!isSessionInDateRange(sessionDate, dateRange)) return;
              if (session.total_session_duration) {
                moduleDurations.push(session.total_session_duration);
              }
            });
          }
        });
      });
      if (moduleDurations.length > 0) {
        lastModuleAvgDuration = moduleDurations.reduce((a, b) => a + b, 0) / moduleDurations.length;
      }
    }

    return {
      totalSessions,
      lastParcoursName,
      lastParcoursId,
      lastParcoursSessionCount: lastParcoursId ? parcoursSessionCounts.get(lastParcoursId) || 0 : 0,
      lastModuleName,
      lastModuleId,
      lastModuleAvgDuration,
      lastSessionDate,
      lastSessionId,
      lastSessionUserName,
    };
  }, [sessionsByProjectGroup, dateRange]);

  // Calculate trend for total sessions
  const sessionsTrend = useMemo(() => {
    // Don't show comparison when "All time" is selected (no date range)
    if (!dateRange.startDate && !dateRange.endDate) {
      return null;
    }

    const currentCount = stats.totalSessions;
    const previousCount = countSessionsInRange(previousPeriod.startDate, previousPeriod.endDate);

    if (previousCount === 0) {
      // Can't calculate percentage change from 0
      return currentCount > 0
        ? {
            value: 100,
            isPositive: true,
            label: previousPeriod.label,
            tooltip: t("statsNew.trend.tooltipNoPrevious"),
          }
        : null;
    }

    const percentChange = Math.round(((currentCount - previousCount) / previousCount) * 100);
    return {
      value: Math.abs(percentChange),
      isPositive: percentChange >= 0,
      label: previousPeriod.label,
      tooltip: t("statsNew.trend.tooltip", {
        current: currentCount,
        previous: previousCount,
      }),
    };
  }, [stats.totalSessions, previousPeriod, countSessionsInRange, t, dateRange]);

  const formatLastSessionDate = () => {
    if (!stats.lastSessionDate) return "-";
    return format(stats.lastSessionDate, "dd MMM yyyy", { locale });
  };

  // Format duration in minutes
  const formatDuration = (seconds: number) => {
    const minutes = Math.round(seconds / 60);
    if (minutes < 1) return t("statsNew.duration.lessThanMinute");
    return t("statsNew.duration.minutes", { count: minutes });
  };

  // Format time ago
  const formatTimeAgo = () => {
    if (!stats.lastSessionDate) return null;
    return formatDistanceToNow(stats.lastSessionDate, { addSuffix: true, locale });
  };

  // Show skeleton during initial load
  if (isInitialLoad) {
    return (
      <div className="space-y-3">
        {/* KPI Cards Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <SkeletonCard key={i} variant="kpi" />
          ))}
        </div>

        {/* Charts Skeleton */}
        <div className="stats-charts-row">
          <div className="stats-chart-card global-chart-container">
            <div className="h-5 w-40 bg-gray-200 rounded animate-pulse mb-4" />
            <SkeletonChart variant="bar" bars={12} />
          </div>
          <div className="stats-chart-card global-chart-container">
            <div className="h-5 w-40 bg-gray-200 rounded animate-pulse mb-4" />
            <SkeletonChart variant="horizontal-bar" bars={5} />
          </div>
        </div>
      </div>
    );
  }

  // Show empty state if no data after loading
  if (!isInitialLoad && stats.totalSessions === 0) {
    // No results due to date range filter (but there are sessions without filter)
    if ((dateRange.startDate || dateRange.endDate) && hasAnySessionsWithoutFilter) {
      return (
        <EmptyState
          icon="calendar"
          title={t("statsNew.emptyState.noData")}
          description={t("statsNew.emptyState.noDataDescription")}
          action={{
            label: t("statsNew.emptyState.viewAll"),
            onClick: handleClearDateRange,
          }}
        />
      );
    }

    // No data at all
    return (
      <EmptyState
        icon="chart"
        title={t("statsNew.emptyState.noData")}
        description={t("statsNew.emptyState.noDataDescription")}
      />
    );
  }

  return (
    <div className={`space-y-3 ${showAlertBanner ? "with-alert" : ""}`}>
      {/* Row 1: KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatsCard
          title={t("statsNew.global.totalSessions")}
          value={stats.totalSessions.toString()}
          subtitle={sessionsTrend?.label}
          trend={
            sessionsTrend
              ? {
                  value: sessionsTrend.value,
                  isPositive: sessionsTrend.isPositive,
                  tooltip: sessionsTrend.tooltip,
                }
              : undefined
          }
          icon={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
          }
        />
        <StatsCard
          title={t("statsNew.global.lastParcours")}
          value={stats.lastParcoursName}
          href={stats.lastParcoursId ? `/statistics/parcours/${stats.lastParcoursId}` : undefined}
          hoverContent={
            stats.lastParcoursSessionCount > 0 && (
              <span className="flex items-center gap-1.5">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
                {t("statsNew.global.sessionsOnParcours", { count: stats.lastParcoursSessionCount })}
              </span>
            )
          }
          icon={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
              />
            </svg>
          }
        />
        <StatsCard
          title={t("statsNew.global.lastModule")}
          value={stats.lastModuleName}
          href={
            stats.lastParcoursId && stats.lastModuleId
              ? `/statistics/parcours/${stats.lastParcoursId}/${stats.lastModuleId}`
              : undefined
          }
          hoverContent={
            stats.lastModuleAvgDuration && (
              <span className="flex items-center gap-1.5">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                {t("statsNew.global.avgDuration", {
                  duration: formatDuration(stats.lastModuleAvgDuration),
                })}
              </span>
            )
          }
          icon={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          }
        />
        <StatsCard
          title={t("statsNew.global.lastSessionDate")}
          value={formatLastSessionDate()}
          href={
            stats.lastParcoursId && stats.lastModuleId && stats.lastSessionId
              ? `/statistics/parcours/${stats.lastParcoursId}/${stats.lastModuleId}?sessionId=${stats.lastSessionId}`
              : undefined
          }
          hoverContent={
            stats.lastSessionDate && (
              <span className="flex items-center gap-1.5">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                {formatTimeAgo()}
                {stats.lastSessionUserName && (
                  <span className="text-gray-400">
                    • {t("statsNew.global.byUser", { name: stats.lastSessionUserName })}
                  </span>
                )}
              </span>
            )
          }
          icon={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          }
        />
      </div>

      {/* Row 2: Activity Heatmap */}
      <div className="stats-chart-card w-full">
        <ActivityHeatmap maxCellSize={24} />
      </div>

      {/* Row 3: Charts - responsive height adapts to screen and alert */}
      <div className="stats-charts-row">
        {/* Sessions per month chart */}
        <div className={`stats-chart-card flex flex-col ${chartContainerClass}`}>
          <h3 className="text-sm font-semibold text-gray-900 mb-2 flex-shrink-0">
            {t("statsNew.charts.sessionsPerMonth")}
          </h3>
          <div className="flex-1 min-h-0">
            <SessionsPerMonthChart dateRange={dateRange} />
          </div>
        </div>

        {/* Top parcours chart */}
        <div className={`stats-chart-card flex flex-col ${chartContainerClass}`}>
          <h3 className="text-sm font-semibold text-gray-900 mb-2 flex-shrink-0">
            {t("statsNew.charts.topParcours")}
          </h3>
          <div className="flex-1 min-h-0">
            <TopParcoursChart dateRange={dateRange} pageSize={pageSize} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalView;
